import { doc, getDoc, runTransaction, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '@/services/firebaseconfig'
import { logEvent } from '@/utils/firebase/eventLogger'

export function powerHubAlertKey(din, name) {
  if (
    typeof din !== 'string' ||
    !din.trim() ||
    din.includes('/') ||
    typeof name !== 'string' ||
    !name.trim()
  ) {
    throw new Error('The alert needs a device ID and name.')
  }
  return `${din}_${name.replace(/[^a-zA-Z0-9_-]/g, '_')}`
}

// The monitor treats the presence of an unexpired record as suppression.
// Restoring removes that record; an ignored:false field would not restore it.
export function activeAlertSuppression(data, now = Date.now()) {
  if (!data) return null
  const expiry = data.expiresAt
  const expiresMs = expiry == null ? null : (expiry.toMillis?.() ?? new Date(expiry).getTime())
  if (expiresMs !== null && (!Number.isFinite(expiresMs) || expiresMs <= now)) return null
  return { ...data, isPermanent: expiresMs === null, expiresMs }
}

export async function readAlertSuppression(din, name) {
  const snapshot = await getDoc(doc(db, 'powerHubIgnoredAlerts', powerHubAlertKey(din, name)))
  return snapshot.exists() ? snapshot.data() : null
}

export async function readPowerHubAlertPolicy() {
  const snapshot = await getDoc(doc(db, 'globalSettings', 'default'))
  const settings = snapshot.data()?.serviceCases || {}
  const days = settings.powerHubAlertsSnoozeDurationDays ?? 7
  if (!Number.isSafeInteger(days) || days < 1) {
    throw new Error(
      'The snooze duration must be a whole number of at least 1 day. Review Service Cases settings.'
    )
  }
  return { days, criticalOverride: settings.powerHubAlertsCriticalOverrideEnabled !== false }
}

export async function changeAlertSuppression({
  din,
  name,
  mode,
  days,
  reason,
  sourceServiceCaseId,
}) {
  const user = auth.currentUser
  if (!user?.uid) throw new Error('Sign in again before changing alert handling.')
  if (!['snooze', 'ignore', 'restore'].includes(mode))
    throw new Error('Choose a valid alert action.')
  const key = powerHubAlertKey(din, name)
  let expiresAt = null
  if (mode === 'snooze') {
    if (!Number.isSafeInteger(days) || days < 1)
      throw new Error('Enter a positive whole number of snooze days.')
    expiresAt = new Date()
    expiresAt.setDate(expiresAt.getDate() + days)
    if (!Number.isFinite(expiresAt.getTime()))
      throw new Error('The snooze date is outside the supported range.')
  }
  const reference = doc(db, 'powerHubIgnoredAlerts', key)
  const change = await runTransaction(db, async transaction => {
    if (auth.currentUser?.uid !== user.uid) throw new Error('Your session changed. Sign in again.')
    const snapshot = await transaction.get(reference)
    if (auth.currentUser?.uid !== user.uid) throw new Error('Your session changed. Sign in again.')
    const before = snapshot.exists() ? snapshot.data() : null
    if (mode === 'restore') {
      if (!before) return null
      transaction.delete(reference)
      return { before, after: null }
    }
    const fields = {
      din,
      alertName: name,
      ignored: true,
      expiresAt,
      reason:
        reason || (mode === 'ignore' ? 'User ignored from Alerts' : `Snoozed for ${days} days`),
      ignoredAt: serverTimestamp(),
      ignoredByUid: user.uid,
      ignoredByEmail: user.email || null,
      ...(sourceServiceCaseId ? { sourceServiceCaseId } : {}),
    }
    transaction.set(reference, fields, { merge: true })
    // Use a serializable time in the audit; the rule uses the server timestamp.
    return { before, after: { ...before, ...fields, ignoredAt: new Date().toISOString() } }
  })
  if (!change) return { changed: false, auditLogged: true }
  try {
    await logEvent({
      eventType: 'POWERHUB_ALERT_SUPPRESSION_UPDATED',
      action: mode === 'restore' ? 'delete' : 'update',
      resource: { type: 'powerHubIgnoredAlerts', id: key },
      changes: { fields: Object.keys(change.after || change.before), ...change },
      context: {
        source: 'frontend',
        metadata: { mode, sourceServiceCaseId: sourceServiceCaseId || null },
      },
      throwOnError: true,
    })
    return { changed: true, auditLogged: true }
  } catch {
    return { changed: true, auditLogged: false }
  }
}
